import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import CachedImage from "./CachedImage";
import ImagePreview from "./ImagePreview";
import { Theme } from "../../../theme";
import { Trash } from "lucide-react";
import ImageResizeService, {
  ImageFormat,
} from "../../../providers/upload/ImageResizeService";

export type ImagePickerValue = string | File | { url?: string } | null;

export interface ImagePickerProps {
  format?: ImageFormat;
  readOnly?: boolean;
  value: ImagePickerValue;
  onSelect: (image: string | File | null) => void;
}

const SUPPORTED_SVG = "image/svg+xml";
const SUPPORTED_FORMATS = ["image/jpeg", "image/png", SUPPORTED_SVG];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ImagePicker: React.FC<ImagePickerProps> = ({
  format = ImageFormat.square,
  readOnly,
  value,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageResizeService = useMemo(() => new ImageResizeService(), []);

  const displayUrl = useMemo(() => {
    if (value instanceof File) return previewUrl;
    if (typeof value === "string" && value.startsWith("http")) return value;
    if (value && typeof value === "object" && "url" in value)
      return value.url ?? null;
    // value peut être le filename → utiliser imageUrl pour l'affichage
    return null;
  }, [value, previewUrl]);

  const displayName = useMemo(() => {
    if (value instanceof File) return value.name;
    return displayUrl ? "image" : null;
  }, [value, displayUrl]);

  useEffect(() => {
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [value]);

  const validateAndSelectFile = useCallback(
    async (file: File) => {
      const isSvg = file.type === SUPPORTED_SVG;
      const isJpgOrPng = SUPPORTED_FORMATS.includes(file.type);

      if (!isSvg && !isJpgOrPng) {
        setError("image.validation.badFormat");
        onSelect(null);
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setError("image.validation.maxFileSize");
        onSelect(null);
        return;
      }

      if (isSvg) {
        setError(null);
        onSelect(file);
        return;
      }

      setIsValidating(true);
      setError(null);

      try {
        const result = await imageResizeService.validateAndResizeImage(
          file,
          format
        );
        if (result.valid) {
          const fileToUse = result.resizedImage ?? file;
          setError(null);
          onSelect(fileToUse);
        } else {
          setError(result.error ?? "image.validation.badImageSize");
          onSelect(null);
        }
      } catch {
        setError("image.validation.badFormat");
        onSelect(null);
      } finally {
        setIsValidating(false);
      }
    },
    [format, imageResizeService, onSelect]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        validateAndSelectFile(file);
      }
      e.target.value = "";
    },
    [validateAndSelectFile]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setError(null);
      onSelect(null);
      if (fileInputRef.current?.value) {
        fileInputRef.current.value = "";
      }
    },
    [onSelect]
  );

  const picker = (
    <div className="flex items-center w-full">
      <div
        className="relative h-10 w-10 shrink-0 overflow-hidden rounded border border-gray-200 bg-gray-100 dark:bg-gray-700"
        style={{ aspectRatio: format }}
      >
        {displayUrl &&
          (displayUrl.startsWith("blob:") ? (
            <img
              key={displayUrl}
              src={displayUrl}
              alt={displayName ?? ""}
              className="h-full w-full object-cover object-center"
              draggable={false}
            />
          ) : (
            <CachedImage
              key={displayUrl}
              src={displayUrl}
              alt={displayName ?? ""}
              className="h-full w-full object-cover object-center"
            />
          ))}
      </div>
      <p
        className={`ms-4 flex-1 min-w-0 text-left truncate w-[220px] ${
          displayName
            ? "text-gray-900 dark:text-gray-100"
            : "text-gray-500 dark:text-gray-600"
        }`}
      >
        {displayName ??
          (readOnly ? t("image.noImageSelected") : t("image.selectImage"))}
      </p>
      <div className="flex-1" />
      {!readOnly && (value || displayUrl) && (
        <button
          type="button"
          className="flex shrink-0 m-1 p-1 text-red-500 hover:text-red-700"
          onClick={handleClear}
        >
          <Trash size={16} />
        </button>
      )}
    </div>
  );

  return (
    <div>
      {readOnly ? (
        <ImagePreview url={displayUrl ?? undefined}>{picker}</ImagePreview>
      ) : (
        <div
          role="button"
          className={`pt-[10px] px-2 flex items-center justify-between select-none h-auto cursor-pointer ${
            Theme.input
          } ${isValidating ? "opacity-70 pointer-events-none" : ""}`}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={SUPPORTED_FORMATS.join(",")}
            onChange={handleFileChange}
            disabled={isValidating}
          />
          {picker}
        </div>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
          {t(error)}
        </p>
      )}
    </div>
  );
};

export default ImagePicker;
